import React, { ReactNode, useEffect } from 'react';
import './App.css';
import { routes } from "./routes";
import { createBrowserRouter, createRoutesFromElements, Navigate, Route, RouterProvider } from 'react-router-dom';
import MainLayout from './components/layout/MainLayout';
import LoginPage from './pages/auth/loginPage';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from './redux/store';
import ForgottenPasswordPage from './pages/auth/ForgottenPasswordPage';
import ResetPasswordPage from './pages/auth/PasswordResetPage';
import AltLayout from './components/layout/AltLayout';
import { Alert, LinearProgress } from "@mui/material";
import { portalHome } from "./routes/appRoutePermissions";
import { USER_TYPES } from './services/userRoles';

import { fetchUserProfile, logout } from "./redux/features/auth/authSlice";

const loginpage = "/login";

interface ProtectedRouteProps {
  children: ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const authData = useSelector((state: RootState) => state.auth);
  const dispatch = useDispatch<AppDispatch>();
  useEffect(() => {
    if (!authData.isAuthenticated || authData.user) return;
    let current = true;
    dispatch(fetchUserProfile()).unwrap().catch(() => {
      if (current) dispatch(logout());
    });
    return () => { current = false; };
  }, [dispatch, authData.isAuthenticated, authData.access, authData.user]);
  if (!authData.isAuthenticated) return <Navigate to={loginpage} replace />;
  if (!authData.user) return <LinearProgress aria-label="Loading profile" />;
  if (authData.user?.user_type === USER_TYPES.appUser) return <Alert severity="warning">App credentials do not provide portal access.</Alert>;
  return <>{children}</>;
};

/** Resolve the permitted landing page whenever the authenticated role changes. */
function HomeRedirect() {
  const authData = useSelector((state: RootState) => state.auth);
  return <Navigate to={portalHome(authData)} replace />;
}

/** Send unknown routes to the authenticated landing page or sign-in. */
function FallbackRedirect() {
  const authData = useSelector((state: RootState) => state.auth);
  return <Navigate to={authData.isAuthenticated ? '/' : loginpage} replace />;
}

/** Use the data router so pages can block all SPA transitions through useBlocker. */
export function createPortalRouter() {
  return createBrowserRouter(createRoutesFromElements(
    <>
        <Route path="/" element={<ProtectedRoute><MainLayout /></ProtectedRoute>}>
          <Route index element={<HomeRedirect />} />
          {routes}
        </Route>
        <Route path="/login" element={<AltLayout />}>
          <Route index element={<LoginPage />} />
        </Route>
        <Route path="/forgot-password" element={<AltLayout />}>
          <Route index element={<ForgottenPasswordPage />} />
        </Route>
        <Route path="/reset/:uidb64/:token" element={<AltLayout />}>
          <Route index element={<ResetPasswordPage />} />
        </Route>
        <Route path="*" element={<FallbackRedirect />} />
    </>
  ));
}

let browserRouter: ReturnType<typeof createPortalRouter> | undefined;

/** Share one production router; allow an isolated router for route integration tests. */
function App({ router }: { router?: ReturnType<typeof createPortalRouter> }) {
  if (!router) {
    browserRouter ??= createPortalRouter();
    router = browserRouter;
  }
  return <RouterProvider router={router} />;
}

export default App;
